import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {
  Card,
  Input,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Modal,
  Tag,
  Collapse,
  Select,
  Upload,
  Image,
  Switch,
  Tabs,
  ColorPicker,
  App,
  Radio,
  Segmented,
  Alert
} from 'antd';
import {
  PlusOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import questionnaireService, { fileToBase64 } from '../services/questionnaireService';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import ImageCropModal from './ImageCropModal';
import {
  DEFAULT_FONT_KEY,
  DEFAULT_QUIZ_BACKGROUND,
  DEFAULT_TEXT_COLOR,
  QUIZ_FONT_OPTIONS,
  QUIZ_TEXT_SIZE_LABELS,
  QUIZ_TEXT_SIZE_OPTIONS,
  getFontStack,
  getTextSizePx
} from '../constants/quizDesign';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const createEditorStateFromHtml = (html) => {
  if (!html) {
    return EditorState.createEmpty();
  }

  const contentBlock = htmlToDraft(html);
  if (!contentBlock) {
    return EditorState.createEmpty();
  }

  const contentState = ContentState.createFromBlockArray(
    contentBlock.contentBlocks,
    contentBlock.entityMap
  );
  return EditorState.createWithContent(contentState);
};

const stripHtmlToPlainText = (html) => {
  if (typeof html !== 'string') {
    return '';
  }

  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const getIntroductionPagePreview = (html) => {
  const text = stripHtmlToPlainText(html);
  if (!text) {
    return 'Empty page';
  }

  return text.length > 50 ? `${text.slice(0, 50)}...` : text;
};

const CROP_CANCELLED_ERROR = 'CROP_CANCELLED';

const parseColorToRgb = (color) => {
  if (typeof color !== 'string') {
    return null;
  }

  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const normalized = hex.length === 3
      ? hex.split('').map((char) => char + char).join('')
      : hex;
    const int = parseInt(normalized, 16);
    if (Number.isNaN(int)) {
      return null;
    }
    return {
      r: (int >> 16) & 255,
      g: (int >> 8) & 255,
      b: int & 255
    };
  }

  const rgbMatch = color.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3])
    };
  }

  return null;
};

const getReadableTextColor = (color, fallback = '#000000') => {
  const rgb = parseColorToRgb(color);
  if (!rgb) {
    return fallback;
  }

  const luminance = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return luminance >= 150 ? '#000000' : '#ffffff';
};

const RICH_TEXT_TOOLBAR = {
  options: ['inline', 'blockType', 'fontSize', 'list', 'link', 'history'],
  inline: { options: ['bold', 'italic', 'underline', 'monospace'] },
  list: { options: ['unordered', 'ordered'] },
  blockType: { inDropdown: true },
  link: { inDropdown: true },
  fontSize: { options: ['12', '14', '16', '18', '24', '32'] },
  textDirection: { options: [] } // Disable text direction controls to prevent RTL issues
};

const IntroductionPageItem = ({ page, pageIndex, onPageChange, onRemovePage, onMovePage, totalPages, onRequestImage }) => {
  const initialPageHtml = page?.text || '';
  const initialPageId = page?.id ?? pageIndex;
  const [pageEditorState, setPageEditorState] = useState(() => createEditorStateFromHtml(initialPageHtml));
  const lastSyncedPageHtmlRef = useRef(initialPageHtml);
  const lastSyncedPageIdRef = useRef(initialPageId);

  useEffect(() => {
    const incomingHtml = page?.text || '';
    const incomingId = page?.id ?? pageIndex;
    if (incomingId !== lastSyncedPageIdRef.current || incomingHtml !== lastSyncedPageHtmlRef.current) {
      setPageEditorState(createEditorStateFromHtml(incomingHtml));
      lastSyncedPageHtmlRef.current = incomingHtml;
      lastSyncedPageIdRef.current = incomingId;
    }
  }, [pageIndex, page?.id, page?.text]);

  const handlePageEditorChange = (state) => {
    setPageEditorState(state);
    const contentState = state.getCurrentContent();
    const html = contentState.hasText()
      ? draftToHtml(convertToRaw(contentState))
      : '';
    lastSyncedPageHtmlRef.current = html;
    onPageChange(pageIndex, 'text', html);
  };

  const handleIntroImageUpload = async ({ file, onSuccess, onError }) => {
    if (!onRequestImage) {
      onError?.(new Error('Image upload is not available right now.'));
      return;
    }

    try {
      const base64 = await onRequestImage(file, {
        title: 'Introduction Page Image',
        aspect: 16 / 9,
        instructions: 'These slides fill a wide desktop frame. Keep the focal point centered to avoid cropping.'
      });
      onPageChange(pageIndex, 'image', base64);
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Page Text */}
      <div style={{ marginBottom: '16px' }}>
        <Text strong>Page Content:</Text>
        <div
          style={{
            marginTop: '4px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            backgroundColor: '#fff'
          }}
          dir="ltr"
        >
          <Editor
            editorState={pageEditorState}
            onEditorStateChange={handlePageEditorChange}
            placeholder="Enter the content for this introduction page..."
            editorStyle={{ minHeight: 160, padding: '0 12px', direction: 'ltr', unicodeBidi: 'plaintext' }}
            toolbar={RICH_TEXT_TOOLBAR}
            toolbarStyle={{ borderBottom: '1px solid #f0f0f0' }}
          />
        </div>
      </div>

      {/* Page Image */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={12}>
          <Text strong>Page Image (Optional):</Text>
          <div style={{ marginTop: '4px' }}>
            <Upload
              accept="image/*"
              showUploadList={false}
              customRequest={handleIntroImageUpload}
            >
              <Button icon={<UploadOutlined />} block>
                {page.image ? 'Change Image' : 'Upload Image'}
              </Button>
            </Upload>
            {page.image && (
              <div style={{ marginTop: '8px', textAlign: 'center' }}>
                <Image src={page.image} width={100} style={{ borderRadius: '4px' }} />
                <br />
                <Button 
                  type="link" 
                  size="small"
                  onClick={() => onPageChange(pageIndex, 'image', null)}
                >
                  Remove Image
                </Button>
              </div>
            )}
          </div>
        </Col>
        <Col span={12}>
          <Text strong>Next Button Text:</Text>
          <Input
            value={page.buttonText}
            onChange={(e) => onPageChange(pageIndex, 'buttonText', e.target.value)}
            placeholder="Text for the Next button (e.g., 'Continue', 'Let's Start')"
            style={{ marginTop: '4px' }}
          />
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              This button will be centered at the bottom of the page
            </Text>
          </div>
        </Col>
      </Row>

      {/* Page Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
        <Space>
          <Button
            size="small"
            onClick={() => onMovePage(pageIndex, pageIndex - 1)}
            disabled={pageIndex === 0}
            icon={<ArrowUpOutlined />}
          >
            Move Up
          </Button>
          <Button
            size="small"
            onClick={() => onMovePage(pageIndex, pageIndex + 1)}
            disabled={pageIndex === totalPages - 1}
            icon={<ArrowDownOutlined />}
          >
            Move Down
          </Button>
        </Space>
        <Button
          size="small"
          danger
          onClick={() => onRemovePage(pageIndex)}
          icon={<DeleteOutlined />}
        >
          Remove Page
        </Button>
      </div>
    </div>
  );
};

const QuestionItem = ({ question, questionIndex, onQuestionChange, onRequestImage }) => {
  const mediaType = question.mediaType || (question.videoUrl ? 'video' : 'image');
  
  // State for explanation editors - keyed by answer index
  const [explanationEditorStates, setExplanationEditorStates] = useState({});
  
  // Initialize editor states for correct answers
  useEffect(() => {
    const newStates = {};
    question.answers.forEach((answer, index) => {
      if (answer.isCorrect && !explanationEditorStates[index]) {
        newStates[index] = createEditorStateFromHtml(answer.explanation || '');
      }
    });
    if (Object.keys(newStates).length > 0) {
      setExplanationEditorStates(prev => ({ ...prev, ...newStates }));
    }
  }, [question.answers]);

  const handleExplanationEditorChange = (answerIndex, editorState) => {
    setExplanationEditorStates(prev => ({ ...prev, [answerIndex]: editorState }));
    const contentState = editorState.getCurrentContent();
    const html = contentState.hasText()
      ? draftToHtml(convertToRaw(contentState))
      : '';
    onQuestionChange(questionIndex, 'answers', question.answers.map((a, i) => 
      i === answerIndex ? { ...a, explanation: html } : a
    ));
  };

  const handleMediaTypeChange = (value) => {
    onQuestionChange(questionIndex, 'mediaType', value);
    if (value === 'video') {
      onQuestionChange(questionIndex, 'image', null);
    } else {
      onQuestionChange(questionIndex, 'videoUrl', '');
    }
  };

  const handleQuestionImageUpload = async ({ file, onSuccess, onError }) => {
    if (!onRequestImage) {
      onError?.(new Error('Image upload is not available right now.'));
      return;
    }

    try {
      const base64 = await onRequestImage(file, {
        title: 'Question Image',
        aspect: 4 / 3,
        instructions: 'Question images are shown above the prompt on desktop. A 4:3 landscape crop keeps them consistent.'
      });
      onQuestionChange(questionIndex, 'image', base64);
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  };

  const handleAnswerImageUpload = (answerIndex) => async ({ file, onSuccess, onError }) => {
    if (!onRequestImage) {
      onError?.(new Error('Image upload is not available right now.'));
      return;
    }

    try {
      const base64 = await onRequestImage(file, {
        title: 'Answer Image',
        aspect: 1,
        instructions: 'Answer images render as small thumbnails. A square crop ensures nothing important is trimmed.'
      });
      onQuestionChange(questionIndex, 'answers', question.answers.map((a, i) => 
        i === answerIndex ? { ...a, image: base64 } : a
      ));
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Question Name */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Text strong>Question Name:</Text>
          <Input
            value={question.name || ''}
            onChange={(e) => onQuestionChange(questionIndex, 'name', e.target.value)}
            placeholder="Enter an internal name (shown in the list)"
            style={{ marginTop: '4px' }}
          />
        </Col>
      </Row>

      {/* Question Text */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Text strong>Question Text:</Text>
          <Input
            value={question.title}
            onChange={(e) => onQuestionChange(questionIndex, 'title', e.target.value)}
            placeholder="Enter your question here..."
            style={{ marginTop: '4px' }}
          />
        </Col>
      </Row>

      {/* Question Description */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Text strong>Description (optional):</Text>
          <Input.TextArea
            value={question.description || ''}
            onChange={(e) => onQuestionChange(questionIndex, 'description', e.target.value)}
            placeholder="Add a short description or hint for this question..."
            rows={2}
            style={{ marginTop: '4px' }}
            maxLength={500}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            This will appear below the question in small text
          </Text>
        </Col>
      </Row>

      {/* Question Type and Media Choice */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={12}>
          <Text strong>Question Type:</Text>
          <Select
            value={question.type}
            onChange={(value) => onQuestionChange(questionIndex, 'type', value)}
            style={{ width: '100%', marginTop: '4px' }}
          >
            <Option value="single">Single Choice (Radio)</Option>
            <Option value="multiple">Multiple Choice (Checkbox)</Option>
          </Select>
        </Col>
        <Col span={12}>
          <Text strong>Question Media:</Text>
          <Radio.Group
            value={mediaType}
            onChange={(e) => handleMediaTypeChange(e.target.value)}
            style={{ width: '100%', marginTop: '4px' }}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="image">Image Upload</Radio.Button>
            <Radio.Button value="video">YouTube Video</Radio.Button>
          </Radio.Group>
        </Col>
      </Row>

      {/* Randomize Answers Toggle */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Space>
            <Switch
              checked={question.randomizeAnswers || false}
              onChange={(checked) => onQuestionChange(questionIndex, 'randomizeAnswers', checked)}
            />
            <Text strong>Randomize answers when displaying this question</Text>
          </Space>
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              When enabled, answer options will appear in random order for each respondent
            </Text>
          </div>
        </Col>
      </Row>

      {mediaType === 'image' && (
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={24}>
            <Text strong>Question Image:</Text>
            <div style={{ marginTop: '4px' }}>
              <Upload
                accept="image/*"
                showUploadList={false}
                customRequest={handleQuestionImageUpload}
              >
                <Button icon={<UploadOutlined />} block>
                  {question.image ? 'Change Image' : 'Upload Image'}
                </Button>
              </Upload>
              {question.image && (
                <div style={{ marginTop: '8px', textAlign: 'center' }}>
                  <Image src={question.image} width={100} style={{ borderRadius: '4px' }} />
                  <br />
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Switch
                        size="small"
                        checked={question.imageVisibility !== 'results'}
                        onChange={(checked) => onQuestionChange(questionIndex, 'imageVisibility', checked ? 'both' : 'results')}
                      />
                      <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        {question.imageVisibility === 'results' ? 'Results only' : 'Show on question'}
                      </Typography.Text>
                    </div>
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => {
                        onQuestionChange(questionIndex, 'image', null);
                        onQuestionChange(questionIndex, 'imageVisibility', 'both');
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}

      {mediaType === 'video' && (
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={24}>
            <Text strong>YouTube Video URL:</Text>
            <Input
              value={question.videoUrl || ''}
              onChange={(e) => onQuestionChange(questionIndex, 'videoUrl', e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              style={{ marginTop: '4px' }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Paste a public YouTube link. When present, the preview shows this video instead of the question image.
            </Text>
            {question.videoUrl && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Switch
                  size="small"
                  checked={question.imageVisibility !== 'results'}
                  onChange={(checked) => onQuestionChange(questionIndex, 'imageVisibility', checked ? 'both' : 'results')}
                />
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  {question.imageVisibility === 'results' ? 'Results only' : 'Show on question'}
                </Typography.Text>
              </div>
            )}
          </Col>
        </Row>
      )}

      {/* Answers */}
      <div>
        <Text strong>Answers:</Text>
        <div style={{ marginTop: '8px' }}>
          {question.answers.map((answer, answerIndex) => (
            <div key={answer.id} style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px',
              padding: '12px', 
              marginBottom: '8px',
              backgroundColor: answer.isCorrect ? '#f6ffed' : '#fafafa'
            }}>
              <Row gutter={8} align="middle">
                <Col flex="none">
                  <Switch
                    checked={answer.isCorrect}
                    onChange={(checked) => onQuestionChange(questionIndex, 'answers', question.answers.map((a, i) => 
                      i === answerIndex ? { ...a, isCorrect: checked } : a
                    ))}
                    checkedChildren="✓"
                    unCheckedChildren="✗"
                    style={{ backgroundColor: answer.isCorrect ? '#52c41a' : undefined }}
                  />
                </Col>
                <Col flex="auto">
                  <Input
                    value={answer.text}
                    onChange={(e) => onQuestionChange(questionIndex, 'answers', question.answers.map((a, i) => 
                      i === answerIndex ? { ...a, text: e.target.value } : a
                    ))}
                    placeholder={`Answer ${answerIndex + 1}`}
                  />
                  {answer.isCorrect && (
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Explanation (optional) - shown when this correct answer is selected
                      </Text>
                      <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', backgroundColor: '#fff' }}>
                        <Editor
                          editorState={explanationEditorStates[answerIndex] || createEditorStateFromHtml(answer.explanation || '')}
                          onEditorStateChange={(state) => handleExplanationEditorChange(answerIndex, state)}
                          placeholder="Enter explanation for why this answer is correct..."
                          editorStyle={{ minHeight: '80px', padding: '8px 12px' }}
                          toolbar={{
                            options: ['inline', 'list', 'link'],
                            inline: {
                              options: ['bold', 'italic', 'underline'],
                            },
                            list: {
                              options: ['unordered', 'ordered'],
                            },
                          }}
                        />
                      </div>
                    </div>
                  )}
                </Col>
                <Col flex="none">
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    customRequest={handleAnswerImageUpload(answerIndex)}
                  >
                    <Button size="small" icon={<UploadOutlined />}>
                      {answer.image ? 'Change' : 'Add'} Image
                    </Button>
                  </Upload>
                </Col>
                <Col flex="none">
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => onQuestionChange(questionIndex, 'answers', question.answers.filter((_, i) => i !== answerIndex))}
                    disabled={question.answers.length <= 2}
                    danger
                    size="small"
                  />
                </Col>
              </Row>
              {answer.image && (
                <div style={{ marginTop: '8px', textAlign: 'center' }}>
                  <Image src={answer.image} width={60} style={{ borderRadius: '4px' }} />
                  <br />
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Switch
                        size="small"
                        checked={answer.imageVisibility !== 'results'}
                        onChange={(checked) => onQuestionChange(questionIndex, 'answers', question.answers.map((a, i) =>
                          i === answerIndex ? { ...a, imageVisibility: checked ? 'both' : 'results' } : a
                        ))}
                      />
                      <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        {answer.imageVisibility === 'results' ? 'Results only' : 'Show on question'}
                      </Typography.Text>
                    </div>
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => onQuestionChange(questionIndex, 'answers', question.answers.map((a, i) => 
                        i === answerIndex ? { ...a, image: null, imageVisibility: 'both' } : a
                      ))}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
          <Button
            type="dashed"
            onClick={() => {
              const newAnswer = {
                id: `a${Date.now()}`,
                text: '',
                image: null,
                imageVisibility: 'both',
                explanation: '',
                isCorrect: false
              };
              onQuestionChange(questionIndex, 'answers', [...question.answers, newAnswer]);
            }}
            block
            icon={<PlusOutlined />}
          >
            Add Answer
          </Button>
        </div>
      </div>
    </div>
  );
};

const QuestionnaireEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { message: messageApi, modal } = App.useApp(); // Use the App context for messages and modals
  const isEditing = Boolean(id && id !== 'new');
  
  const [questionnaire, setQuestionnaire] = useState({
    title: '',
    description: '',
    context: '',
    image: null,
    backgroundColor: DEFAULT_QUIZ_BACKGROUND,
    backgroundImage: null,
    textColor: DEFAULT_TEXT_COLOR,
    fontFamily: DEFAULT_FONT_KEY,
    welcomeTextSize: 'md',
    questionTextSize: 'md',
    buttonColor: '',
    buttonTextColor: '',
    introductionPages: [],
    questions: [],
    isPublic: false,
    status: 'draft'
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [contextEditorState, setContextEditorState] = useState(() => createEditorStateFromHtml(''));
  const [cropTask, setCropTask] = useState(null);
  const [designTab, setDesignTab] = useState('font');

  // AI import state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [aiImportMode, setAiImportMode] = useState('append');
  const [aiFabHover, setAiFabHover] = useState(false);

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const autoSaveIdRef = useRef(id && id !== 'new' ? id : null); // Track the ID (may change from null to new ID on first save)
  const initialLoadDoneRef = useRef(false);
  const lastSavedJsonRef = useRef(null); // Track what was last saved to avoid no-op saves

  // Concurrent editing state
  const serverUpdatedAtRef = useRef(null); // updatedAt from last server load/save
  const [conflictInfo, setConflictInfo] = useState(null); // { savedByName, serverUpdatedAt, pendingData }
  const [otherEditors, setOtherEditors] = useState([]); // presence: [{ name }]
  const presenceTimerRef = useRef(null);
  const fallbackButtonColor = questionnaire.textColor || '#ffffff';
  const previewButtonColor = questionnaire.buttonColor || fallbackButtonColor;
  const previewButtonTextColor = questionnaire.buttonTextColor || getReadableTextColor(previewButtonColor, '#000000');
  const previewFontFamily = getFontStack(questionnaire.fontFamily);
  const previewWelcomeFontSize = getTextSizePx('welcome', questionnaire.welcomeTextSize);
  const previewQuestionFontSize = getTextSizePx('question', questionnaire.questionTextSize);
  const previewAnswerFontSize = getTextSizePx('answer', questionnaire.questionTextSize);
  const previewBackgroundStyles = questionnaire.backgroundImage
    ? {
        backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.55), rgba(0,0,0,0.35)), url(${questionnaire.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    : {
        background: questionnaire.backgroundColor
      };
  const fontSelectOptions = QUIZ_FONT_OPTIONS.map((font) => ({
    label: font.label,
    value: font.value
  }));
  const textSizeSelectorOptions = QUIZ_TEXT_SIZE_OPTIONS.map((size) => ({
    label: QUIZ_TEXT_SIZE_LABELS[size],
    value: size
  }));

  const openCropperForFile = (file, options = {}) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        setCropTask({
          imageSrc: typeof reader.result === 'string' ? reader.result : '',
          originalFile: file,
          options,
          resolve,
          reject
        });
      };
      reader.onerror = () => reject(new Error('Failed to read the selected image.'));
      reader.readAsDataURL(file);
    });
  };

  const handleCropModalCancel = () => {
    if (cropTask?.reject) {
      cropTask.reject(new Error(CROP_CANCELLED_ERROR));
    }
    setCropTask(null);
  };

  const handleCropModalUseOriginal = () => {
    if (cropTask?.resolve) {
      cropTask.resolve(cropTask.originalFile);
    }
    setCropTask(null);
  };

  const handleCropModalApply = (blob) => {
    if (cropTask?.resolve) {
      const type = blob?.type || cropTask.originalFile?.type || 'image/jpeg';
      const fileName = cropTask.originalFile?.name || 'cropped-image.jpg';
      const croppedFile = blob ? new File([blob], fileName, { type }) : cropTask.originalFile;
      cropTask.resolve(croppedFile);
    }
    setCropTask(null);
  };

  const processImageUpload = async (file, options = {}) => {
    try {
      const shouldCrop = options?.skipCrop ? false : true;
      const workingFile = shouldCrop ? await openCropperForFile(file, options) : file;
      const base64 = await fileToBase64(workingFile, options?.maxSizeKB || 1024);
      if (!options?.silent) {
        messageApi.success(options?.successMessage || 'Image saved successfully');
      }
      return base64;
    } catch (error) {
      if (error?.message !== CROP_CANCELLED_ERROR) {
        console.error('Image upload failed:', error);
        messageApi.error(options?.errorMessage || 'Failed to process the image. Please try again.');
      }
      throw error;
    }
  };

  // Introduction pages handlers
  const handleIntroPageChange = (pageIndex, field, value) => {
    const currentPages = questionnaire?.introductionPages || [];
    const updatedPages = [...currentPages];
    updatedPages[pageIndex] = {
      ...updatedPages[pageIndex],
      [field]: value
    };
    setQuestionnaire(prev => ({
      ...prev,
      introductionPages: updatedPages
    }));
  };

  const handleAddIntroPage = () => {
    const newPage = {
      id: `intro_${Date.now()}`,
      text: '',
      image: null,
      buttonText: 'Next'
    };
    setQuestionnaire(prev => ({
      ...prev,
      introductionPages: [...(prev.introductionPages || []), newPage]
    }));
  };

  const handleRemoveIntroPage = (pageIndex) => {
    const currentPages = questionnaire?.introductionPages || [];
    if (currentPages.length > 0) {
      const updatedPages = currentPages.filter((_, index) => index !== pageIndex);
      setQuestionnaire(prev => ({
        ...prev,
        introductionPages: updatedPages
      }));
    }
  };

  const handleMoveIntroPage = (fromIndex, toIndex) => {
    const currentPages = questionnaire?.introductionPages || [];
    if (toIndex < 0 || toIndex >= currentPages.length) return;
    
    const updatedPages = [...currentPages];
    const [movedPage] = updatedPages.splice(fromIndex, 1);
    updatedPages.splice(toIndex, 0, movedPage);
    
    setQuestionnaire(prev => ({
      ...prev,
      introductionPages: updatedPages
    }));
  };

  const handleContextEditorChange = (state) => {
    setContextEditorState(state);
    const contentState = state.getCurrentContent();
    const html = contentState.hasText()
      ? draftToHtml(convertToRaw(contentState))
      : '';
    setQuestionnaire(prev => ({
      ...prev,
      context: html
    }));
  };
  
  // Initialize with one empty question for new questionnaires
  useEffect(() => {
    if (!isEditing) {
      setQuestionnaire(prev => ({
        ...prev,
        questions: [{
          id: `q${Date.now()}`,
          name: '',
          title: '',
          type: 'single',
          image: null,
          imageFocus: { x: 50, y: 50 },
          answers: [
            { id: `a${Date.now()}_1`, text: '', image: null, explanation: '', isCorrect: false },
            { id: `a${Date.now()}_2`, text: '', image: null, explanation: '', isCorrect: false }
          ]
        }]
      }));
    }
  }, [isEditing]);
  
  // Load questionnaire data if editing
  useEffect(() => {
    if (isEditing && id) {
      const fetchQuestionnaire = async () => {
        try {
          setLoading(true);
          const response = await questionnaireService.getById(id);
          if (response.success) {
            const normalizedQuestionnaire = {
              ...response.data,
              backgroundColor: response.data.backgroundColor || DEFAULT_QUIZ_BACKGROUND,
              backgroundImage: response.data.backgroundImage || null,
              textColor: response.data.textColor || DEFAULT_TEXT_COLOR,
              fontFamily: response.data.fontFamily || DEFAULT_FONT_KEY,
              welcomeTextSize: response.data.welcomeTextSize || 'md',
              questionTextSize: response.data.questionTextSize || 'md',
              buttonColor: response.data.buttonColor || '',
              buttonTextColor: response.data.buttonTextColor || '',
              introductionPages: response.data.introductionPages || [],
              questions: (response.data.questions || []).map((question) => ({
                ...question,
                imageFocus: question.imageFocus || { x: 50, y: 50 }
              }))
            };
            setQuestionnaire(normalizedQuestionnaire);
            setContextEditorState(createEditorStateFromHtml(response.data.context));
            serverUpdatedAtRef.current = response.data.updatedAt || null;
          } else {
            messageApi.error('Failed to load quiz');
            navigate('/dashboard/questionnaires');
          }
        } catch (error) {
          console.error('Error loading questionnaire:', error);
          messageApi.error('Failed to load quiz');
          navigate('/dashboard/questionnaires');
        } finally {
          setLoading(false);
        }
      };
      
      fetchQuestionnaire();
    }
  }, [isEditing, id, navigate]);

  // Auto-save: perform the draft save
  const performAutoSave = useCallback(async (data) => {
    // Don't auto-save while a manual save is in progress
    if (saving) return;

    // Minimum requirement: must have a title (even placeholder is fine)
    if (!data.title || !data.title.trim()) return;

    // Skip if nothing changed since last save
    const currentJson = JSON.stringify({
      title: data.title,
      description: data.description,
      context: data.context,
      image: data.image,
      backgroundColor: data.backgroundColor,
      textColor: data.textColor,
      fontFamily: data.fontFamily,
      welcomeTextSize: data.welcomeTextSize,
      questionTextSize: data.questionTextSize,
      buttonColor: data.buttonColor,
      buttonTextColor: data.buttonTextColor,
      introductionPages: data.introductionPages,
      questions: data.questions,
    });
    if (currentJson === lastSavedJsonRef.current) {
      setHasUnsavedChanges(false);
      return;
    }

    try {
      setAutoSaveStatus('saving');
      const payload = { ...data, clientUpdatedAt: serverUpdatedAtRef.current };
      const response = await questionnaireService.draftSave(autoSaveIdRef.current, payload);
      if (response.conflict) {
        // Someone else saved — stop auto-save and show conflict modal
        setAutoSaveStatus('error');
        setConflictInfo({ savedByName: response.savedByName, serverUpdatedAt: response.serverUpdatedAt, pendingData: data });
        return;
      }
      if (response.success) {
        lastSavedJsonRef.current = currentJson;
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        // Keep serverUpdatedAt in sync
        if (response.data?.updatedAt) serverUpdatedAtRef.current = response.data.updatedAt;
        // If this was a new questionnaire, update the ID for future saves
        if (!autoSaveIdRef.current && response.data?._id) {
          autoSaveIdRef.current = response.data._id;
          // Update URL without full navigation so user doesn't lose state
          window.history.replaceState(null, '', `/dashboard/questionnaires/edit/${response.data._id}`);
        }
      } else {
        setAutoSaveStatus('error');
      }
    } catch (err) {
      console.error('Auto-save failed:', err);
      setAutoSaveStatus('error');
    }
  }, [saving]);

  // Auto-save: debounce trigger on questionnaire changes
  useEffect(() => {
    // Don't start auto-saving until initial load is done
    if (!initialLoadDoneRef.current) return;

    setHasUnsavedChanges(true);
    setAutoSaveStatus('idle');

    // Clear previous timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new debounce timer (5 seconds after last change)
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave(questionnaire);
    }, 5000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [questionnaire, performAutoSave]);

  // Mark initial load as done after data is loaded
  useEffect(() => {
    if (loading) return;
    // Small delay to avoid triggering auto-save on initial state
    const timer = setTimeout(() => {
      initialLoadDoneRef.current = true;
      // Snapshot initial state
      lastSavedJsonRef.current = JSON.stringify({
        title: questionnaire.title,
        description: questionnaire.description,
        context: questionnaire.context,
        image: questionnaire.image,
        backgroundColor: questionnaire.backgroundColor,
        textColor: questionnaire.textColor,
        fontFamily: questionnaire.fontFamily,
        welcomeTextSize: questionnaire.welcomeTextSize,
        questionTextSize: questionnaire.questionTextSize,
        buttonColor: questionnaire.buttonColor,
        buttonTextColor: questionnaire.buttonTextColor,
        introductionPages: questionnaire.introductionPages,
        questions: questionnaire.questions,
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [loading]); // Only depends on loading state transition

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  // Presence: ping while editing an existing quiz, leave on unmount
  useEffect(() => {
    const quizId = autoSaveIdRef.current;
    if (!quizId || !isEditing) return;

    const ping = async () => {
      const result = await questionnaireService.presencePing(quizId);
      if (result?.editors) setOtherEditors(result.editors);
    };

    ping(); // immediate ping on mount
    presenceTimerRef.current = setInterval(ping, 30000);

    return () => {
      clearInterval(presenceTimerRef.current);
      questionnaireService.presenceLeave(quizId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const handleQuestionChange = (questionIndex, field, value) => {
    setQuestionnaire(prev => {
      const updatedQuestions = [...(prev.questions || [])];
      const currentQuestion = updatedQuestions[questionIndex] || {};
      const nextQuestion = {
        ...currentQuestion,
        [field]: value
      };

      if (field === 'image' && !value) {
        nextQuestion.imageFocus = { x: 50, y: 50 };
        nextQuestion.imageVisibility = 'both';
      }

      if (!nextQuestion.imageFocus) {
        nextQuestion.imageFocus = { x: 50, y: 50 };
      }

      updatedQuestions[questionIndex] = nextQuestion;
      return {
        ...prev,
        questions: updatedQuestions
      };
    });
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `q${Date.now()}`,
      name: '',
      title: '',
      description: '',
      randomizeAnswers: false,
      type: 'single',
      image: null,
      imageFocus: { x: 50, y: 50 },
      answers: [
        { id: `a${Date.now()}_1`, text: '', image: null, explanation: '', isCorrect: false },
        { id: `a${Date.now()}_2`, text: '', image: null, explanation: '', isCorrect: false }
      ]
    };
    setQuestionnaire(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const handleDeleteQuestion = (questionIndex) => {
    if (questionnaire.questions.length > 1) {
      const updatedQuestions = questionnaire.questions.filter((_, index) => index !== questionIndex);
      setQuestionnaire(prev => ({
        ...prev,
        questions: updatedQuestions
      }));
    }
  };

  const handleMoveQuestion = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= questionnaire.questions.length) return;
    
    const updatedQuestions = [...questionnaire.questions];
    const [movedQuestion] = updatedQuestions.splice(fromIndex, 1);
    updatedQuestions.splice(toIndex, 0, movedQuestion);
    
    setQuestionnaire(prev => ({
      ...prev,
      questions: updatedQuestions
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!questionnaire.title.trim()) {
      messageApi.error('Please enter a quiz title');
      return;
    }

    if (!questionnaire.image) {
      messageApi.error('Please upload a Quiz Image before saving');
      return;
    }

    if (questionnaire.questions.length === 0) {
      messageApi.error('Please add at least one question');
      return;
    }

    // Validate each question
    for (let i = 0; i < questionnaire.questions.length; i++) {
      const question = questionnaire.questions[i];
      
      if (!question.title.trim()) {
        messageApi.error(`Question ${i + 1}: Please enter question text`);
        return;
      }

      if (question.answers.length < 2) {
        messageApi.error(`Question ${i + 1}: Please add at least 2 answers`);
        return;
      }

      const hasCorrectAnswer = question.answers.some(answer => answer.isCorrect);
      if (!hasCorrectAnswer) {
        messageApi.error(`Question ${i + 1}: Please mark at least one answer as correct`);
        return;
      }

      // Check if all answers have text or image
      for (let j = 0; j < question.answers.length; j++) {
        const answer = question.answers[j];
        const hasText = answer.text && answer.text.trim();
        const hasImage = answer.image;
        
        if (!hasText && !hasImage) {
          messageApi.error(`Question ${i + 1}, Answer ${j + 1}: Please enter answer text or add an image`);
          return;
        }
      }
    }

    try {
      setSaving(true);
      // Cancel any pending auto-save
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      const payload = { ...questionnaire, clientUpdatedAt: serverUpdatedAtRef.current };
      if (!payload.buttonColor || !payload.buttonColor.trim()) {
        delete payload.buttonColor;
      }
      if (!payload.buttonTextColor || !payload.buttonTextColor.trim()) {
        delete payload.buttonTextColor;
      }
      let response;

      // Use existing ID from auto-save if available
      const effectiveId = isEditing ? id : autoSaveIdRef.current;

      if (effectiveId) {
        response = await questionnaireService.update(effectiveId, payload);
      } else {
        response = await questionnaireService.create(payload);
      }

      if (response?.conflict) {
        setSaving(false);
        setConflictInfo({ savedByName: response.savedByName, serverUpdatedAt: response.serverUpdatedAt, pendingData: questionnaire });
        return;
      }

      if (response.success) {
        // Update auto-save tracking
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        if (response.data?.updatedAt) serverUpdatedAtRef.current = response.data.updatedAt;
        lastSavedJsonRef.current = JSON.stringify({
          title: questionnaire.title,
          description: questionnaire.description,
          context: questionnaire.context,
          image: questionnaire.image,
          backgroundColor: questionnaire.backgroundColor,
          textColor: questionnaire.textColor,
          fontFamily: questionnaire.fontFamily,
          welcomeTextSize: questionnaire.welcomeTextSize,
          questionTextSize: questionnaire.questionTextSize,
          buttonColor: questionnaire.buttonColor,
          buttonTextColor: questionnaire.buttonTextColor,
          introductionPages: questionnaire.introductionPages,
          questions: questionnaire.questions,
        });

        messageApi.success(`Quiz ${effectiveId ? 'saved' : 'created'} successfully!`);
        
        // Determine the correct ID for status toggle
        const savedId = effectiveId || response.data?._id;
        
        // If editing a draft, ask if they want to complete it
        if (savedId && questionnaire.status === 'draft') {
          modal.confirm({
            title: 'Mark Quiz as Complete?',
            content: 'Would you like to mark this quiz as complete so it can be used in campaigns?',
            okText: 'Yes, Complete',
            cancelText: 'No, Stay as Draft',
            onOk: async () => {
              try {
                const completeResponse = await questionnaireService.togglePublish(savedId, 'complete');
                if (completeResponse.success) {
                  messageApi.success('Quiz marked as complete!');
                }
              } catch (err) {
                console.error('Error completing quiz:', err);
              }
              navigate('/dashboard/questionnaires');
            },
            onCancel: () => {
              navigate('/dashboard/questionnaires');
            }
          });
        } else {
          navigate('/dashboard/questionnaires');
        }
      } else {
        messageApi.error(response.message || 'Failed to save quiz');
      }
    } catch (error) {
      console.error('Error saving quiz:', error);
      const apiMessage = error.response?.data?.message || error.response?.data?.error;
      const validationErrors = error.response?.data?.errors;
      if (validationErrors && typeof validationErrors === 'object') {
        const firstError = Object.values(validationErrors)[0];
        messageApi.error(firstError || apiMessage || 'Failed to save quiz. Please try again.');
      } else {
        messageApi.error(apiMessage || 'Failed to save quiz. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!hasUnsavedChanges) {
      navigate('/dashboard/questionnaires');
      return;
    }
    modal.confirm({
      title: 'Leave Editor?',
      content: autoSaveIdRef.current 
        ? 'Your latest draft has been auto-saved. Any changes since the last auto-save will be lost.'
        : 'Are you sure you want to leave? Any unsaved changes will be lost.',
      okText: 'Yes, Leave',
      cancelText: 'Stay',
      onOk() {
        navigate('/dashboard/questionnaires');
      }
    });
  };

  const handleStatusChange = async (action) => {
    if (action === 'complete' && !questionnaire.image) {
      messageApi.error('Please upload a Quiz Image before marking the quiz as complete');
      return;
    }
    try {
      setSaving(true);
      const response = await questionnaireService.togglePublish(id, action);
      if (response.success) {
        // Update local state
        setQuestionnaire(prev => ({
          ...prev,
          status: response.data.status
        }));
        
        messageApi.success(`Quiz ${action}d successfully`);
        
        // Redirect to questionnaires list after marking as complete
        if (action === 'complete') {
          navigate('/dashboard/questionnaires');
        }
      } else {
        messageApi.error(response.message || 'Failed to change quiz status');
      }
    } catch (error) {
      console.error('Error changing quiz status:', error);
      messageApi.error('Failed to change quiz status');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!isEditing || !id) {
      messageApi.warning('Please save the quiz first to preview it');
      return;
    }

    // Save before previewing so edits aren't lost
    try {
      setSaving(true);
      const payload = { ...questionnaire };
      if (!payload.buttonColor || !payload.buttonColor.trim()) {
        delete payload.buttonColor;
      }
      if (!payload.buttonTextColor || !payload.buttonTextColor.trim()) {
        delete payload.buttonTextColor;
      }
      const response = await questionnaireService.update(id, payload);
      if (response.success) {
        messageApi.success('Quiz saved');
      } else {
        messageApi.error(response.message || 'Failed to save quiz before preview');
        return;
      }
    } catch (error) {
      console.error('Error saving quiz before preview:', error);
      messageApi.error('Failed to save quiz before preview');
      return;
    } finally {
      setSaving(false);
    }

    // Open preview in new tab
    window.open(`/preview/${id}`, '_blank');
  };

  const handleAiParse = async () => {
    if (!aiText.trim()) { messageApi.warning('Please paste some quiz text first'); return; }
    setAiParsing(true);
    try {
      const result = await questionnaireService.parseQuizText(aiText.trim());
      if (!result.success) { messageApi.error(result.message || 'Failed to parse quiz text'); return; }

      const rawData = result.data || {};
      const rawQuestions = Array.isArray(rawData) ? rawData : (rawData.questions || []);
      const aiTitle = rawData.title || '';
      const aiDescription = rawData.description || '';
      const aiContext = rawData.context || '';
      const aiIntroPages = (rawData.introductionPages || []).map((p, i) => ({
        id: `intro_${Date.now()}_${i}`,
        text: p.text || '',
        image: null,
        buttonText: p.buttonText || 'Start',
      }));

      const parsed = rawQuestions.map((q, idx) => {
        const answers = (q.answers || []).map((a, ai) => ({
          id: `a${Date.now()}_${idx}_${ai}`,
          text: a.text || '',
          image: null,
          explanation: a.isCorrect ? (q.description || '') : '',
          isCorrect: !!a.isCorrect,
        }));
        return {
          id: `q${Date.now()}_${idx}`,
          name: q.name || `Q${idx + 1}`,
          title: q.title || '',
          description: '',
          type: q.type || 'single',
          image: null,
          imageFocus: { x: 50, y: 50 },
          answers,
        };
      });

      if (!parsed.length) { messageApi.warning('No questions found in the pasted text'); return; }

      setQuestionnaire(prev => {
        const applyTitle = aiTitle && (aiImportMode === 'replace' || !prev.title?.trim());
        const applyDescription = aiDescription && (aiImportMode === 'replace' || !prev.description?.trim());
        const applyContext = aiContext && (aiImportMode === 'replace' || !prev.context?.trim());
        const applyIntro = aiIntroPages.length > 0 && (aiImportMode === 'replace' || !(prev.introductionPages || []).length);
        if (applyContext) {
          setContextEditorState(createEditorStateFromHtml(aiContext));
        }
        return {
          ...prev,
          title: applyTitle ? aiTitle : prev.title,
          description: applyDescription ? aiDescription : prev.description,
          context: applyContext ? aiContext : prev.context,
          introductionPages: applyIntro ? aiIntroPages : (prev.introductionPages || []),
          questions: aiImportMode === 'replace'
            ? parsed
            : [...prev.questions.filter(q => q.title.trim()), ...parsed],
        };
      });

      setAiModalOpen(false);
      setAiText('');
      setActiveTab('questions');

      const parts = [`${parsed.length} question${parsed.length !== 1 ? 's' : ''}`];
      if (aiTitle) parts.push('quiz name');
      if (aiDescription) parts.push('description');
      if (aiContext) parts.push('context');
      if (aiIntroPages.length) parts.push('introduction page');
      messageApi.success(`Imported: ${parts.join(', ')}`);
    } catch {
      messageApi.error('Something went wrong — please try again');
    } finally {
      setAiParsing(false);
    }
  };

  // ── Conflict resolution handlers ──────────────────────────────────────────
  const handleConflictOverwrite = async () => {
    if (!conflictInfo) return;
    // Force-save by not sending clientUpdatedAt (no conflict check)
    const data = conflictInfo.pendingData;
    const effectiveId = isEditing ? id : autoSaveIdRef.current;
    try {
      setSaving(true);
      const payload = { ...data };
      if (!payload.buttonColor?.trim()) delete payload.buttonColor;
      if (!payload.buttonTextColor?.trim()) delete payload.buttonTextColor;
      const response = effectiveId
        ? await questionnaireService.update(effectiveId, payload)
        : await questionnaireService.create(payload);
      if (response.success) {
        if (response.data?.updatedAt) serverUpdatedAtRef.current = response.data.updatedAt;
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        messageApi.success('Quiz saved (overwritten)');
      }
    } catch (err) {
      messageApi.error('Save failed');
    } finally {
      setSaving(false);
      setConflictInfo(null);
    }
  };

  const handleConflictReload = async () => {
    const effectiveId = isEditing ? id : autoSaveIdRef.current;
    if (!effectiveId) { setConflictInfo(null); return; }
    try {
      const response = await questionnaireService.getById(effectiveId);
      if (response.success) {
        setQuestionnaire({
          ...response.data,
          backgroundColor: response.data.backgroundColor || DEFAULT_QUIZ_BACKGROUND,
          backgroundImage: response.data.backgroundImage || null,
          textColor: response.data.textColor || DEFAULT_TEXT_COLOR,
          fontFamily: response.data.fontFamily || DEFAULT_FONT_KEY,
          welcomeTextSize: response.data.welcomeTextSize || 'md',
          questionTextSize: response.data.questionTextSize || 'md',
          buttonColor: response.data.buttonColor || '',
          buttonTextColor: response.data.buttonTextColor || '',
          introductionPages: response.data.introductionPages || [],
          questions: (response.data.questions || []).map(q => ({ ...q, imageFocus: q.imageFocus || { x: 50, y: 50 } })),
        });
        setContextEditorState(createEditorStateFromHtml(response.data.context));
        serverUpdatedAtRef.current = response.data.updatedAt || null;
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        lastSavedJsonRef.current = null;
        messageApi.info('Quiz reloaded with latest changes');
      }
    } catch (err) {
      messageApi.error('Failed to reload quiz');
    }
    setConflictInfo(null);
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div>Loading quiz...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        title={isEditing ? 'Edit Quiz' : 'Create New Quiz'}
        subtitle={isEditing ? 'Modify your quiz details and questions' : 'Build your quiz with questions and answers'}
        breadcrumbs={['Dashboard', 'Quizzes', isEditing ? 'Edit' : 'New']}
      />

      {/* ── Presence banner: someone else is editing ── */}
      {otherEditors.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            otherEditors.length === 1
              ? `${otherEditors[0].name} is also editing this quiz — saving may overwrite their changes.`
              : `${otherEditors.map(e => e.name).join(' and ')} are also editing this quiz.`
          }
        />
      )}

      {/* ── Conflict modal: someone saved while you were editing ── */}
      <Modal
        open={!!conflictInfo}
        title="Quiz edited by someone else"
        onCancel={() => setConflictInfo(null)}
        footer={null}
        width={480}
      >
        {conflictInfo && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type="error"
              showIcon
              message={
                conflictInfo.savedByName
                  ? `${conflictInfo.savedByName} saved this quiz after you opened it.`
                  : 'This quiz was saved by another user after you opened it.'
              }
              description={
                conflictInfo.serverUpdatedAt
                  ? `Their version was saved at ${new Date(conflictInfo.serverUpdatedAt).toLocaleTimeString()}. Your unsaved changes are shown in the editor.`
                  : 'Your unsaved changes are still in the editor.'
              }
            />
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleConflictReload}>
                Discard my changes &amp; reload
              </Button>
              <Button type="primary" danger onClick={handleConflictOverwrite} loading={saving}>
                Overwrite with my changes
              </Button>
            </Space>
          </Space>
        )}
      </Modal>

      {/* Tabbed Interface */}
      <Card style={{ marginBottom: '24px' }}>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'basic',
              label: 'Basic Information',
              children: (
                <div>
                  <Row gutter={16}>
                    <Col span={16}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong>Title *</Text>
                        <Input
                          value={questionnaire.title}
                          onChange={(e) => setQuestionnaire(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Enter quiz title"
                          style={{ marginTop: '4px' }}
                        />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong>Public Access</Text>
                        <div style={{ marginTop: '4px' }}>
                          <Switch
                            checked={questionnaire.isPublic}
                            onChange={(checked) => setQuestionnaire(prev => ({ ...prev, isPublic: checked }))}
                            checkedChildren="Public"
                            unCheckedChildren="Private"
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong>Description</Text>
                    <Input.TextArea
                      value={questionnaire.description}
                      onChange={(e) => setQuestionnaire(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the quiz"
                      rows={3}
                      style={{ marginTop: '4px' }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong>Introduction/Context</Text>
                    <div
                      style={{
                        marginTop: '4px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        backgroundColor: '#fff'
                      }}
                      dir="ltr"
                    >
                      <Editor
                        editorState={contextEditorState}
                        onEditorStateChange={handleContextEditorChange}
                        placeholder="Introduction text shown to quiz takers"
                        editorStyle={{ minHeight: 180, padding: '0 12px', direction: 'ltr', unicodeBidi: 'plaintext' }}
                        toolbar={RICH_TEXT_TOOLBAR}
                        toolbarStyle={{ borderBottom: '1px solid #f0f0f0' }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong>Quiz Image <span style={{ color: '#ff4d4f' }}>*</span></Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: '8px', fontSize: '12px' }}>
                      This image displays on the quiz introduction. A separate 16:9 version can be created for WhatsApp templates.
                    </Text>
                    <div style={{ marginTop: '8px' }}>
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        customRequest={async ({ file, onSuccess, onError }) => {
                          try {
                            // Allow user to crop to any ratio for quiz display
                            const base64 = await processImageUpload(file, {
                              title: 'Quiz Cover Image',
                              allowAspectSelection: true,
                              instructions: 'Choose any aspect ratio for quiz display. You\'ll be prompted to create a 16:9 version for WhatsApp afterwards.',
                              successMessage: 'Cover image updated'
                            });
                            
                            // Get the aspect ratio of the uploaded image
                            const img = new window.Image();
                            img.onload = async () => {
                              const aspectRatio = img.width / img.height;
                              const is16by9 = Math.abs(aspectRatio - (16/9)) < 0.1;
                              
                              setQuestionnaire(prev => ({ ...prev, image: base64 }));
                              
                              // If not 16:9, prompt for WhatsApp crop
                              if (!is16by9) {
                                Modal.confirm({
                                  title: 'Create WhatsApp Version?',
                                  content: (
                                    <div>
                                      <p>Your image is not 16:9 ratio. WhatsApp template headers require 16:9 landscape images.</p>
                                      <p>Would you like to create a cropped version specifically for WhatsApp?</p>
                                    </div>
                                  ),
                                  okText: 'Create WhatsApp Version',
                                  cancelText: 'Skip',
                                  onOk: async () => {
                                    try {
                                      const whatsappBase64 = await processImageUpload(file, {
                                        title: 'WhatsApp Template Image',
                                        aspect: 16 / 9,
                                        allowAspectSelection: false,
                                        instructions: 'Crop to 16:9 for WhatsApp template header. Keep key content centered.',
                                        successMessage: 'WhatsApp image created'
                                      });
                                      setQuestionnaire(prev => ({ ...prev, whatsappImage: whatsappBase64 }));
                                    } catch (err) {
                                      // User cancelled or error - that's ok
                                    }
                                  }
                                });
                              } else {
                                // Already 16:9, use same image for both
                                setQuestionnaire(prev => ({ ...prev, whatsappImage: base64 }));
                              }
                            };
                            img.src = base64;
                            
                            onSuccess?.();
                          } catch (error) {
                            onError?.(error);
                          }
                        }}
                      >
                        <Button icon={<UploadOutlined />} block>
                          {questionnaire.image ? 'Change Image' : 'Upload Image'}
                        </Button>
                      </Upload>
                      {questionnaire.image && (
                        <div style={{ marginTop: '12px' }}>
                          <Row gutter={16}>
                            <Col span={12} style={{ textAlign: 'center' }}>
                              <Text type="secondary" style={{ display: 'block', marginBottom: '4px', fontSize: '11px' }}>Quiz Display</Text>
                              <Image src={questionnaire.image} width={120} style={{ borderRadius: '8px' }} />
                            </Col>
                            <Col span={12} style={{ textAlign: 'center' }}>
                              <Text type="secondary" style={{ display: 'block', marginBottom: '4px', fontSize: '11px' }}>WhatsApp Header</Text>
                              {questionnaire.whatsappImage ? (
                                <Image src={questionnaire.whatsappImage} width={120} style={{ borderRadius: '8px' }} />
                              ) : (
                                <div style={{ padding: '20px', background: '#f5f5f5', borderRadius: '8px', fontSize: '11px', color: '#999' }}>
                                  No 16:9 version
                                  <br />
                                  <Button 
                                    type="link" 
                                    size="small"
                                    onClick={async () => {
                                      try {
                                        // Convert base64 back to file for cropping
                                        const response = await fetch(questionnaire.image);
                                        const blob = await response.blob();
                                        const file = new File([blob], 'image.jpg', { type: blob.type });
                                        
                                        const whatsappBase64 = await processImageUpload(file, {
                                          title: 'WhatsApp Template Image',
                                          aspect: 16 / 9,
                                          allowAspectSelection: false,
                                          instructions: 'Crop to 16:9 for WhatsApp template header.',
                                          successMessage: 'WhatsApp image created'
                                        });
                                        setQuestionnaire(prev => ({ ...prev, whatsappImage: whatsappBase64 }));
                                      } catch (err) {
                                        // User cancelled
                                      }
                                    }}
                                  >
                                    Create now
                                  </Button>
                                </div>
                              )}
                            </Col>
                          </Row>
                          <div style={{ textAlign: 'center', marginTop: '8px' }}>
                            <Button 
                              type="link" 
                              size="small"
                              danger
                              onClick={() => setQuestionnaire(prev => ({ ...prev, image: null, whatsappImage: null }))}
                            >
                              Remove All Images
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            },
            {
              key: 'introduction-pages',
              label: `Introduction Pages (${questionnaire?.introductionPages?.length || 0})`,
              children: (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <Row gutter={16} align="middle">
                      <Col flex="1">
                        <Text type="secondary">
                          Create additional introduction pages that will be shown after the main introduction but before the questions start. 
                          Each page can contain text, an image, and a customizable button.
                        </Text>
                      </Col>
                      <Col>
                        <Button 
                          type="primary" 
                          icon={<PlusOutlined />} 
                          onClick={handleAddIntroPage}
                        >
                          Add Introduction Page
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  
                  {questionnaire?.introductionPages?.length > 0 ? (
                    <Collapse defaultActiveKey={(questionnaire.introductionPages || []).map((_, index) => index.toString())}>
                      {(questionnaire.introductionPages || []).map((page, index) => (
                        <Panel 
                          header={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>
                                <strong>Page {index + 1}:</strong> {getIntroductionPagePreview(page.text)}
                              </span>
                              <Space size="small" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveIntroPage(index, index - 1);
                                  }}
                                  disabled={index === 0}
                                  icon={<ArrowUpOutlined />}
                                />
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveIntroPage(index, index + 1);
                                  }}
                                  disabled={index === (questionnaire?.introductionPages?.length || 0) - 1}
                                  icon={<ArrowDownOutlined />}
                                />
                                <Button
                                  size="small"
                                  danger
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveIntroPage(index);
                                  }}
                                  icon={<DeleteOutlined />}
                                />
                              </Space>
                            </div>
                          }
                          key={index.toString()}
                        >
                          <IntroductionPageItem
                            page={page}
                            pageIndex={index}
                            onPageChange={handleIntroPageChange}
                            onRemovePage={handleRemoveIntroPage}
                            onMovePage={handleMoveIntroPage}
                            totalPages={questionnaire?.introductionPages?.length || 0}
                            onRequestImage={processImageUpload}
                          />
                        </Panel>
                      ))}
                    </Collapse>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px 20px',
                      border: '2px dashed #d9d9d9',
                      borderRadius: '6px',
                      backgroundColor: '#fafafa'
                    }}>
                      <InboxOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                      <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                        No introduction pages yet. Click "Add Introduction Page" to create your first page.
                      </Text>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={handleAddIntroPage}
                      >
                        Add Your First Page
                      </Button>
                    </div>
                  )}
                </div>
              )
            },
            {
              key: 'design',
              label: 'Design',
              children: (
                <div>
                  <Tabs
                    activeKey={designTab}
                    onChange={setDesignTab}
                    items={[
                      {
                        key: 'font',
                        label: 'Font',
                        children: (
                          <div>
                            <Row gutter={24}>
                              <Col span={12}>
                                <Text strong style={{ display: 'block', marginBottom: '8px' }}>Font Family</Text>
                                <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                                  Choose a preset that feels closest to your brand. It will cascade across intro, questions, and results.
                                </Text>
                                <Select
                                  value={questionnaire.fontFamily || DEFAULT_FONT_KEY}
                                  onChange={(value) => setQuestionnaire(prev => ({ ...prev, fontFamily: value }))}
                                  options={fontSelectOptions}
                                  style={{ width: '100%' }}
                                  placeholder="Select a font"
                                />
                                <div
                                  style={{
                                    marginTop: '12px',
                                    padding: '16px',
                                    borderRadius: '8px',
                                    border: '1px solid #f0f0f0',
                                    backgroundColor: '#ffffff'
                                  }}
                                >
                                  <div
                                    style={{
                                      fontFamily: previewFontFamily,
                                      fontSize: `${previewWelcomeFontSize}px`,
                                      fontWeight: 600,
                                      marginBottom: '8px'
                                    }}
                                  >
                                    Quiz headings look like this
                                  </div>
                                  <div
                                    style={{
                                      fontFamily: previewFontFamily,
                                      fontSize: `${previewQuestionFontSize}px`,
                                      color: '#595959'
                                    }}
                                  >
                                    And your question copy follows this style.
                                  </div>
                                </div>
                              </Col>
                              <Col span={12}>
                                <Text strong style={{ display: 'block', marginBottom: '8px' }}>Title & Question Color</Text>
                                <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                                  Defaults to your business unit palette if left empty.
                                </Text>
                                <Row gutter={8}>
                                  <Col span={16}>
                                    <Input
                                      value={questionnaire.textColor}
                                      onChange={(e) => setQuestionnaire(prev => ({ ...prev, textColor: e.target.value }))}
                                      placeholder="CSS color value"
                                      addonBefore="Text"
                                    />
                                  </Col>
                                  <Col span={8}>
                                    <ColorPicker
                                      value={questionnaire.textColor || DEFAULT_TEXT_COLOR}
                                      onChange={(color) => setQuestionnaire(prev => ({ ...prev, textColor: color.toHexString() }))}
                                      style={{ width: '100%' }}
                                      presets={[{
                                        label: 'Common',
                                        colors: ['#ffffff', '#000000', '#f0f0f0', '#595959', '#001529', '#722ed1', '#52c41a', '#fa541c']
                                      }]}
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                            <Row gutter={24} style={{ marginTop: '24px' }}>
                              <Col span={12}>
                                <Text strong style={{ display: 'block', marginBottom: '8px' }}>Welcome screen & results size</Text>
                                <Segmented
                                  value={questionnaire.welcomeTextSize || 'md'}
                                  onChange={(value) => setQuestionnaire(prev => ({ ...prev, welcomeTextSize: value }))}
                                  options={textSizeSelectorOptions}
                                  block
                                />
                              </Col>
                              <Col span={12}>
                                <Text strong style={{ display: 'block', marginBottom: '8px' }}>Question text size</Text>
                                <Segmented
                                  value={questionnaire.questionTextSize || 'md'}
                                  onChange={(value) => setQuestionnaire(prev => ({ ...prev, questionTextSize: value }))}
                                  options={textSizeSelectorOptions}
                                  block
                                />
                              </Col>
                            </Row>
                          </div>
                        )
                      },
                      {
                        key: 'buttons',
                        label: 'Buttons',
                        children: (
                          <Row gutter={24}>
                            <Col span={12}>
                              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Button Color</Text>
                              <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                                Applies to primary actions across intro, questions, and the score screen.
                              </Text>
                              <Row gutter={8}>
                                <Col span={16}>
                                  <Input
                                    value={questionnaire.buttonColor}
                                    onChange={(e) => setQuestionnaire(prev => ({ ...prev, buttonColor: e.target.value }))}
                                    placeholder="CSS color value"
                                    addonBefore="Button"
                                  />
                                </Col>
                                <Col span={8}>
                                  <ColorPicker
                                    value={(() => {
                                      if (questionnaire.buttonColor?.startsWith('#')) {
                                        return questionnaire.buttonColor;
                                      }
                                      if (typeof fallbackButtonColor === 'string' && fallbackButtonColor.startsWith('#')) {
                                        return fallbackButtonColor;
                                      }
                                      return '#1890ff';
                                    })()}
                                    onChange={(color) => setQuestionnaire(prev => ({ ...prev, buttonColor: color.toHexString() }))}
                                    style={{ width: '100%' }}
                                    presets={[{
                                      label: 'Button presets',
                                      colors: ['#1890ff', '#52c41a', '#fa8c16', '#eb2f96', '#13c2c2', '#722ed1']
                                    }]}
                                  />
                                </Col>
                              </Row>
                              <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                                Leave blank to inherit the text color ({fallbackButtonColor}).
                              </Text>
                            </Col>
                            <Col span={12}>
                              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Button Text Color</Text>
                              <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                                Override the text/icon color that sits on the button.
                              </Text>
                              <Row gutter={8}>
                                <Col span={16}>
                                  <Input
                                    value={questionnaire.buttonTextColor}
                                    onChange={(e) => setQuestionnaire(prev => ({ ...prev, buttonTextColor: e.target.value }))}
                                    placeholder="CSS color value"
                                    addonBefore="Button Text"
                                  />
                                </Col>
                                <Col span={8}>
                                  <ColorPicker
                                    value={(questionnaire.buttonTextColor && questionnaire.buttonTextColor.startsWith('#')) ? questionnaire.buttonTextColor : '#ffffff'}
                                    onChange={(color) => setQuestionnaire(prev => ({ ...prev, buttonTextColor: color.toHexString() }))}
                                    style={{ width: '100%' }}
                                    presets={[{
                                      label: 'Legible picks',
                                      colors: ['#ffffff', '#000000', '#f0f0f0', '#262626', '#ebf4ff', '#111D4A']
                                    }]}
                                  />
                                </Col>
                              </Row>
                              <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                                Leave blank to auto-detect a readable contrast.
                              </Text>
                            </Col>
                          </Row>
                        )
                      },
                      {
                        key: 'background',
                        label: 'Background',
                        children: (
                          <div>
                            <Row gutter={24}>
                              <Col span={12}>
                                <Text strong style={{ display: 'block', marginBottom: '8px' }}>Background Color or Gradient</Text>
                                <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                                  Supports solid colors or CSS gradients. Falls back to the business unit palette when empty.
                                </Text>
                                <Row gutter={8}>
                                  <Col span={16}>
                                    <Input
                                      value={questionnaire.backgroundColor}
                                      onChange={(e) => setQuestionnaire(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                      placeholder="CSS color value or gradient"
                                      addonBefore="Background"
                                    />
                                  </Col>
                                  <Col span={8}>
                                    <ColorPicker
                                      value={(typeof questionnaire.backgroundColor === 'string' && questionnaire.backgroundColor.startsWith('#')) ? questionnaire.backgroundColor : '#52c41a'}
                                      onChange={(color) => setQuestionnaire(prev => ({ ...prev, backgroundColor: color.toHexString() }))}
                                      style={{ width: '100%' }}
                                      presets={[{
                                        label: 'Recommended',
                                        colors: ['#52c41a', '#1890ff', '#722ed1', '#eb2f96', '#fa541c', '#13c2c2', '#faad14']
                                      }]}
                                    />
                                  </Col>
                                </Row>
                                <div style={{ marginTop: '12px' }}>
                                  <Text strong style={{ display: 'block', marginBottom: '8px', fontSize: '12px' }}>Gradient Presets</Text>
                                  <Row gutter={[8, 8]}>
                                    <Col span={8}>
                                      <Button
                                        size="small"
                                        block
                                        onClick={() => setQuestionnaire(prev => ({ ...prev, backgroundColor: DEFAULT_QUIZ_BACKGROUND }))}
                                        style={{ background: DEFAULT_QUIZ_BACKGROUND, color: '#fff', border: 'none' }}
                                      >
                                        Green
                                      </Button>
                                    </Col>
                                    <Col span={8}>
                                      <Button
                                        size="small"
                                        block
                                        onClick={() => setQuestionnaire(prev => ({ ...prev, backgroundColor: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)' }))}
                                        style={{ background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)', color: '#fff', border: 'none' }}
                                      >
                                        Blue
                                      </Button>
                                    </Col>
                                    <Col span={8}>
                                      <Button
                                        size="small"
                                        block
                                        onClick={() => setQuestionnaire(prev => ({ ...prev, backgroundColor: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)' }))}
                                        style={{ background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)', color: '#fff', border: 'none' }}
                                      >
                                        Purple
                                      </Button>
                                    </Col>
                                  </Row>
                                </div>
                              </Col>
                              <Col span={12}>
                                <Text strong style={{ display: 'block', marginBottom: '8px' }}>Background Image</Text>
                                <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                                  Optional hero texture that sits behind every screen. Go for abstract imagery so the text stays readable.
                                </Text>
                                <Upload
                                  accept="image/*"
                                  showUploadList={false}
                                  customRequest={async ({ file, onSuccess, onError }) => {
                                    try {
                                      const base64 = await processImageUpload(file, {
                                        title: 'Quiz Background Image',
                                        instructions: 'Use landscape images (1920px+) for the best result. A subtle blur works great.',
                                        allowAspectSelection: true,
                                        successMessage: 'Background image updated',
                                        maxSizeKB: 2048
                                      });
                                      setQuestionnaire(prev => ({ ...prev, backgroundImage: base64 }));
                                      onSuccess?.();
                                    } catch (error) {
                                      onError?.(error);
                                    }
                                  }}
                                >
                                  <Button icon={<UploadOutlined />} block>
                                    {questionnaire.backgroundImage ? 'Change Background Image' : 'Upload Background Image'}
                                  </Button>
                                </Upload>
                                {questionnaire.backgroundImage && (
                                  <div style={{ marginTop: '12px', textAlign: 'center' }}>
                                    <Image src={questionnaire.backgroundImage} width={140} style={{ borderRadius: '6px' }} />
                                    <br />
                                    <Button
                                      type="link"
                                      size="small"
                                      onClick={() => setQuestionnaire(prev => ({ ...prev, backgroundImage: null }))}
                                    >
                                      Remove image
                                    </Button>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </div>
                        )
                      }
                    ]}
                  />
                  <div style={{ marginTop: '24px' }}>
                    <Text strong style={{ display: 'block', marginBottom: '12px' }}>Preview</Text>
                    <div
                      style={{
                        ...previewBackgroundStyles,
                        color: questionnaire.textColor || DEFAULT_TEXT_COLOR,
                        padding: '28px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        fontFamily: previewFontFamily,
                        boxShadow: '0 16px 40px rgba(0,0,0,0.12)'
                      }}
                    >
                      <Title
                        level={3}
                        style={{
                          color: questionnaire.textColor || DEFAULT_TEXT_COLOR,
                          marginBottom: '12px',
                          fontFamily: previewFontFamily,
                          fontSize: `${previewWelcomeFontSize}px`
                        }}
                      >
                        {questionnaire.title || 'Sample Quiz Title'}
                      </Title>
                      <Text
                        style={{
                          color: questionnaire.textColor || DEFAULT_TEXT_COLOR,
                          opacity: 0.9,
                          fontSize: `${previewQuestionFontSize}px`,
                          display: 'block',
                          marginBottom: '20px'
                        }}
                      >
                        This is how your intro copy will appear to participants.
                      </Text>
                      <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                        <Text strong style={{ display: 'block', marginBottom: '8px', color: questionnaire.textColor || DEFAULT_TEXT_COLOR }}>
                          Sample Question
                        </Text>
                        <Text style={{ display: 'block', marginBottom: '4px', color: questionnaire.textColor || DEFAULT_TEXT_COLOR, fontSize: `${previewAnswerFontSize}px`, opacity: 0.85 }}>
                          • Option one answer text
                        </Text>
                        <Text style={{ display: 'block', color: questionnaire.textColor || DEFAULT_TEXT_COLOR, fontSize: `${previewAnswerFontSize}px`, opacity: 0.85 }}>
                          • Option two answer text
                        </Text>
                      </div>
                      <Button
                        style={{
                          backgroundColor: previewButtonColor,
                          borderColor: previewButtonColor,
                          color: previewButtonTextColor,
                          fontFamily: previewFontFamily,
                          fontWeight: 600,
                          fontSize: `${previewAnswerFontSize}px`,
                          padding: '0 32px',
                          height: '44px'
                        }}
                      >
                        Start Quiz
                      </Button>
                    </div>
                  </div>
                </div>
              )
            },
            {
              key: 'questions',
              label: `Questions (${questionnaire.questions.length})`,
              children: (
                <div>
                  <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />} 
                      onClick={handleAddQuestion}
                    >
                      Add Question
                    </Button>
                  </div>
                  
                  {questionnaire.questions.length > 0 ? (
                    <Collapse defaultActiveKey={questionnaire.questions.map((_, index) => index.toString())}>
                      {questionnaire.questions.map((question, index) => (
                        <Panel 
                          header={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>
                                <strong>Question {index + 1}:</strong> {question.name || question.title || 'Untitled Question'}
                              </span>
                              <Space size="small" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<ArrowUpOutlined />}
                                  onClick={() => handleMoveQuestion(index, index - 1)}
                                  disabled={index === 0}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<ArrowDownOutlined />}
                                  onClick={() => handleMoveQuestion(index, index + 1)}
                                  disabled={index === questionnaire.questions.length - 1}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleDeleteQuestion(index)}
                                  disabled={questionnaire.questions.length <= 1}
                                  danger
                                />
                              </Space>
                            </div>
                          }
                          key={index.toString()}
                        >
                          <QuestionItem
                            question={question}
                            questionIndex={index}
                            onQuestionChange={handleQuestionChange}
                            onRequestImage={processImageUpload}
                          />
                        </Panel>
                      ))}
                    </Collapse>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      <QuestionCircleOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                      <div>No questions added yet</div>
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleAddQuestion} style={{ marginTop: '16px' }}>
                        Add Your First Question
                      </Button>
                    </div>
                  )}
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* Actions */}
      <Card>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button onClick={handleCancel} size="large" disabled={saving}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                onClick={handleSave}
                loading={saving}
                size="large"
              >
                {isEditing || autoSaveIdRef.current ? 'Save' : 'Create'} Quiz
              </Button>
              
              {/* Auto-save status indicator */}
              <Text type="secondary" style={{ fontSize: '13px', marginLeft: '4px' }}>
                {saving ? '' : 
                  autoSaveStatus === 'saving' ? '⏳ Saving draft...' :
                  autoSaveStatus === 'saved' && !hasUnsavedChanges ? '✓ Draft saved' :
                  autoSaveStatus === 'error' ? '⚠ Auto-save failed' :
                  hasUnsavedChanges ? '● Unsaved changes' : ''}
              </Text>
              
              {/* Preview and Quiz buttons - only show if editing */}
              {isEditing && (
                <>
                  <Button
                    icon={<EyeOutlined />}
                    onClick={handlePreview}
                    size="large"
                    disabled={saving}
                    style={{ 
                      backgroundColor: saving ? undefined : '#52c41a', 
                      borderColor: saving ? undefined : '#52c41a',
                      color: saving ? undefined : 'white'
                    }}
                  >
                    Preview
                  </Button>
                </>
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              {isEditing && questionnaire.status === 'draft' && (
                <Button
                 
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleStatusChange('complete')}
                  loading={saving}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  Mark Complete
                </Button>
              )}
              
              {isEditing && questionnaire.status === 'complete' && (
                <Button
                  icon={<InboxOutlined />}
                  onClick={() => handleStatusChange('archive')}
                  loading={saving}
                  style={{ color: '#8c8c8c', borderColor: '#8c8c8c' }}
                >
                  Archive
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      <ImageCropModal
        open={Boolean(cropTask)}
        imageSrc={cropTask?.imageSrc}
        title={cropTask?.options?.title}
        instructions={cropTask?.options?.instructions}
        aspect={cropTask?.options?.aspect}
        allowAspectSelection={cropTask?.options?.allowAspectSelection !== false}
        onCancel={handleCropModalCancel}
        onApply={handleCropModalApply}
        onUseOriginal={handleCropModalUseOriginal}
      />

      {/* AI Import FAB */}
      <div
        onClick={() => setAiModalOpen(true)}
        onMouseEnter={() => setAiFabHover(true)}
        onMouseLeave={() => setAiFabHover(false)}
        title="Import questions from text using AI"
        style={{
          position: 'fixed', bottom: 32, right: 32, zIndex: 1000,
          width: 56, height: 56, borderRadius: '50%',
          background: 'linear-gradient(135deg, #267a96 0%, #1A3A6B 100%)',
          boxShadow: '0 6px 20px rgba(38,122,150,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          transform: aiFabHover ? 'scale(1.1)' : 'scale(1)',
          transition: 'transform 0.18s ease',
        }}
      >
        <span style={{ fontSize: 22, color: '#fff', lineHeight: 1 }}>✦</span>
      </div>

      {/* AI Import Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18, color: '#267a96' }}>✦</span>
            <span>Import Questions with AI</span>
          </div>
        }
        open={aiModalOpen}
        onCancel={() => { if (!aiParsing) { setAiModalOpen(false); setAiText(''); } }}
        footer={null}
        width={680}
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
          Paste your quiz text below. The AI will extract each question, all answer options,
          mark the correct answer, and include the explanation.
        </div>

        <Input.TextArea
          value={aiText}
          onChange={e => setAiText(e.target.value)}
          placeholder={`Paste your quiz text here, e.g.\n\nQ1: Which model comes with AMG Line?\nA. GLC 220d\nB. GLC 300\nAnswer: A\nExplanation: The GLC 220d features the AMG Line as standard…`}
          rows={14}
          style={{ fontFamily: 'monospace', fontSize: 12, marginBottom: 16 }}
          disabled={aiParsing}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Radio.Group
            value={aiImportMode}
            onChange={e => setAiImportMode(e.target.value)}
            disabled={aiParsing}
            size="small"
          >
            <Radio value="append">Add to existing questions</Radio>
            <Radio value="replace">Replace all questions</Radio>
          </Radio.Group>

          <Space>
            <Button onClick={() => { setAiModalOpen(false); setAiText(''); }} disabled={aiParsing}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleAiParse}
              loading={aiParsing}
              style={{ background: '#267a96', borderColor: '#267a96' }}
            >
              {aiParsing ? 'Analysing…' : '✦ Parse & Import'}
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default QuestionnaireEditor;

